/**
 * Cmd-K omnibox overlay. A native <dialog> (top layer — escapes any ancestor overflow/transform/
 * z-index clipping, same rationale as the base Modal) with a single search input in its header and the
 * shared SearchResults below. Opening focuses the input; selecting a result navigates via drillPath
 * and closes; Escape / backdrop click close. Motion uses the token durations (reduced-motion safe).
 *
 * Mounted once at the layout level so Cmd-K works from any route. The same useSearch hook + drill
 * routing back the /search page, so the two surfaces never drift.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { IconButton } from "@/components/ui/IconButton";
import { drillPath, type DrillTarget } from "./drill";
import { useSearch } from "./useSearch";
import { SearchResults } from "./SearchResults";

export interface OmniboxProps {
  open: boolean;
  onClose: () => void;
}

export function Omnibox({ open, onClose }: OmniboxProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState("");

  const search = useSearch(term);

  // Sync the native dialog open state with the controlled prop.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    let raf: number | undefined;
    if (open && !dialog.open) {
      dialog.showModal();
      // Defer focus until after the dialog is in the top layer. Cancelled on cleanup, matching
      // Select/AppShell/dateFieldShared's popover: a close within the same frame would otherwise
      // steal focus back into an input that is on its way out.
      raf = requestAnimationFrame(() => inputRef.current?.focus());
    } else if (!open && dialog.open) {
      dialog.close();
    }
    return () => {
      if (raf !== undefined) cancelAnimationFrame(raf);
    };
  }, [open]);

  // Clear the term whenever the overlay closes so it reopens fresh.
  useEffect(() => {
    if (!open) setTerm("");
  }, [open]);

  // Route native cancel (Escape) / close back to the controlled onClose.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handle = () => onClose();
    dialog.addEventListener("cancel", handle);
    dialog.addEventListener("close", handle);
    return () => {
      dialog.removeEventListener("cancel", handle);
      dialog.removeEventListener("close", handle);
    };
  }, [onClose]);

  function handleSelect(target: DrillTarget) {
    onClose();
    navigate(drillPath(target));
  }

  function onBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === dialogRef.current) onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={onBackdropClick}
      aria-label={t("search.omniboxLabel")}
      className={cn(
        "mx-auto mt-[12vh] w-[calc(100vw-2rem)] max-w-2xl rounded-(--radius-lg) border border-(--border)",
        "bg-(--surface-raised) p-0 text-(--text) shadow-(--shadow-lg)",
        "backdrop:bg-[oklch(0.3_0.02_265_/_0.4)] backdrop:backdrop-blur-sm",
        "open:animate-[modal-in_var(--dur)_var(--ease-out)]",
        "motion-reduce:open:animate-none",
      )}
    >
      {/* Search input header */}
      <div className="flex items-center gap-2 border-b border-(--border-hairline) px-4 py-3.5">
        <Search className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />
        <input
          ref={inputRef}
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            // A type="search" input swallows Escape (browser clears the field) which suppresses the
            // native <dialog> cancel event — handle it explicitly so Escape always closes the omnibox,
            // whether the field is empty or not.
            if (e.key === "Escape") {
              e.preventDefault();
              onClose();
            }
          }}
          placeholder={t("shell.omniboxPlaceholder")}
          aria-label={t("search.inputLabel")}
          autoComplete="off"
          spellCheck={false}
          className={cn(
            "h-7 w-full flex-1 bg-transparent text-(length:--text-base) text-(--text)",
            "placeholder:text-(--text-placeholder) focus:outline-none",
          )}
        />
        {search.isFetching && search.active && (
          <span className="text-(length:--text-2xs) text-(--text-subtle)">
            {t("state.loading")}
          </span>
        )}
        <IconButton
          label={t("action.close")}
          icon={<X className="size-4" />}
          size="sm"
          onClick={onClose}
        />
      </div>

      {/* Results */}
      <div className="max-h-[60vh] overflow-y-auto px-1 py-2">
        <SearchResults
          term={search.debounced}
          active={search.active}
          canSearch={search.canSearch}
          data={search.data}
          isLoading={search.isLoading}
          isError={search.isError}
          error={search.error}
          onSelect={handleSelect}
          onRetry={search.refetch}
          density="overlay"
        />
      </div>
    </dialog>
  );
}
